import { type BenefitPlanGroup } from "@platform/btx-plan-designer";
import { isUnmappedPageId } from "@platform/mpx-quotes-grid";
import { type CoverageTypeEnum } from "@platform/types";
import { useCallback, useMemo, useState } from "react";

interface Coverage {
  type: CoverageTypeEnum;
  planGroups?: unknown[];
}

// `rfpCoverages` (from the RFP) isn't in the dropdown's display order — that
// order comes from `availableCoverageTypes` (the coverage-overview endpoint,
// which is already sorted). Default to its first entry that's actually one
// of the RFP's coverages, falling back to `rfpCoverages[0]` while it's
// loading or empty.
const findDefaultCoverageType = (
  rfpCoverages: Coverage[],
  availableCoverageTypes: CoverageTypeEnum[],
): CoverageTypeEnum | undefined => {
  const rfpCoverageTypes = new Set(
    rfpCoverages.map((coverage) => coverage.type),
  );

  return (
    availableCoverageTypes.find((type) => rfpCoverageTypes.has(type)) ??
    rfpCoverages[0].type
  );
};

interface UseCoverageSelectionResult {
  selectedCoverageType: CoverageTypeEnum | undefined;
  selectedBpgId: string | undefined;
  selectedBenefitPlanGroup: BenefitPlanGroup | undefined;
  handlePlanSelect: (coverageType: CoverageTypeEnum, bpgId?: string) => void;
}

export const useCoverageSelection = (params: {
  rfpCoverages: Coverage[] | undefined;
  availableCoverageTypes: CoverageTypeEnum[];
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >;
  initialCoverageType?: CoverageTypeEnum;
  initialBpgId?: string;
}): UseCoverageSelectionResult => {
  const {
    rfpCoverages,
    availableCoverageTypes,
    benefitPlanGroupsByCoverage,
    initialCoverageType,
    initialBpgId,
  } = params;

  const [internalCoverageType, setInternalCoverageType] = useState<
    CoverageTypeEnum | undefined
  >(initialCoverageType);
  const [internalBpgId, setInternalBpgId] = useState<string | undefined>(
    initialBpgId,
  );

  const handlePlanSelect = useCallback(
    (coverageType: CoverageTypeEnum, bpgId?: string) => {
      setInternalCoverageType(coverageType);
      setInternalBpgId(bpgId);
    },
    [],
  );

  const selectedCoverageType = useMemo(() => {
    if (!rfpCoverages?.length) {
      return undefined;
    }
    let coverageType: CoverageTypeEnum | undefined = undefined;

    if (internalCoverageType) {
      coverageType = rfpCoverages.find(
        (coverage) => coverage.type === internalCoverageType,
      )?.type;
    }

    if (!coverageType) {
      coverageType = findDefaultCoverageType(
        rfpCoverages,
        availableCoverageTypes,
      );
    }

    return coverageType;
  }, [rfpCoverages, availableCoverageTypes, internalCoverageType]);

  const selectedBpgId = useMemo(() => {
    if (!selectedCoverageType) {
      return undefined;
    }
    const benefitPlanGroups = benefitPlanGroupsByCoverage[selectedCoverageType];
    if (internalBpgId) {
      const benefitPlanGroupId = benefitPlanGroups?.find(
        (group) => group.id === internalBpgId,
      )?.id;
      if (benefitPlanGroupId) {
        return benefitPlanGroupId;
      }
    }

    // Never default to the synthetic "Unmapped Plans" page (btx-unmapped-plans)
    // — it's reachable via the picker or a deep link, not as a starting point.
    return benefitPlanGroups?.find((group) => !isUnmappedPageId(group.id))?.id;
  }, [benefitPlanGroupsByCoverage, selectedCoverageType, internalBpgId]);

  const selectedBenefitPlanGroup = useMemo(() => {
    if (!selectedCoverageType || !selectedBpgId) {
      return undefined;
    }
    return benefitPlanGroupsByCoverage[selectedCoverageType]?.find(
      (group) => group.id === selectedBpgId,
    );
  }, [benefitPlanGroupsByCoverage, selectedBpgId, selectedCoverageType]);

  return {
    selectedCoverageType,
    selectedBpgId,
    selectedBenefitPlanGroup,
    handlePlanSelect,
  };
};
